import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";
import { useLang } from "@/i18n/LanguageProvider";

export default function Header() {
  const { t } = useTranslation();
  const { prefix } = useLang();
  const NAV = [
    { to: "/how-it-works", label: t("nav.howItWorks") },
    { to: "/fees", label: t("nav.fees") },
    { to: "/countries", label: t("nav.corridors") },
    { to: "/security", label: t("nav.security") },
    { to: "/press", label: t("nav.press") },
    { to: "/faq", label: t("nav.faq") },
  ];
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [loc.pathname]);

  return (
    <header
      className={`fixed top-9 inset-x-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-canvas/80 border-b border-forest/10"
          : "bg-transparent border-b border-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="container-x flex items-center justify-between h-16 md:h-20">
        <Link to={prefix("/")} className="flex items-center gap-2" data-testid="brand-link">
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={prefix(n.to)}
              data-testid={`nav-${n.to.replace("/", "")}`}
              className={({ isActive }) =>
                `px-3.5 py-2 rounded-full text-sm transition-colors ${
                  isActive ? "bg-forest text-canvas" : "text-ink/70 hover:text-ink"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <Link to={prefix("/contact")} className="hidden md:inline-flex btn-ghost text-sm py-2.5 px-4" data-testid="nav-contact">
            {t("nav.contact")}
          </Link>
          <Link to={prefix("/waitlist")} className="btn-accent text-sm py-2.5 px-4" data-testid="nav-waitlist-cta">
            {t("nav.joinWaitlist")} <ArrowUpRight className="w-4 h-4" strokeWidth={2.25} />
          </Link>
          <button
            aria-label={t("nav.openMenu")}
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden ml-1 h-10 w-10 rounded-full border border-forest/15 flex items-center justify-center"
          >
            {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden bg-canvas border-b border-forest/10"
          >
            <div className="container-x py-4 flex flex-col gap-1">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`mnav-${n.to.replace("/", "")}`}
                  className={({ isActive }) =>
                    `px-3 py-3 rounded-2xl text-base ${isActive ? "bg-forest text-canvas" : "text-ink/80"}`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <Link to="/contact" className="px-3 py-3 rounded-2xl text-base text-ink/80" data-testid="mnav-contact">
                {t("nav.contact")}
              </Link>
              <div className="hairline my-2" />
              <div className="px-1 pb-2">
                <LanguageSwitcher variant="mobile" />
              </div>
              <Link to="/waitlist" className="btn-accent w-full justify-center" data-testid="mnav-waitlist-cta">
                {t("nav.joinWaitlistLong")} <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

function Logo() {
  const [animate, setAnimate] = useState(false);
  useEffect(() => {
    // Play the arrows-crossing animation only on the very first mount per session
    try {
      if (sessionStorage.getItem("ix.logo.animated") !== "1") {
        setAnimate(true);
        sessionStorage.setItem("ix.logo.animated", "1");
      }
    } catch (_) { /* ignore */ }
  }, []);

  const line = (x1, y1, x2, y2, corner) => {
    // corner: [dx, dy] direction the arrow flies in FROM
    const [dx, dy] = corner;
    return (
      <motion.line
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        markerEnd="url(#ix-ah)"
        initial={animate ? { opacity: 0, x: dx * 24, y: dy * 24 } : false}
        animate={{ opacity: 1, x: 0, y: 0 }}
        transition={{ duration: 0.55, ease: [0.76, 0, 0.24, 1], delay: 0.05 }}
      />
    );
  };

  return (
    <div className="flex items-center gap-2.5" data-testid="brand-logo">
      <svg
        width="32"
        height="32"
        viewBox="0 0 32 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-label="Instantxfer CashApp — Power X logo"
      >
        <defs>
          <linearGradient id="ix-grad" x1="0" y1="0" x2="32" y2="32" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#7C3AED" />
            <stop offset="1" stopColor="#2D0D5E" />
          </linearGradient>
          <marker
            id="ix-ah"
            viewBox="0 0 6 6"
            refX="1.2"
            refY="3"
            markerWidth="4"
            markerHeight="4"
            orient="auto"
            markerUnits="strokeWidth"
          >
            <path d="M0,0 L6,3 L0,6 L2,3 Z" fill="#E8FF00" />
          </marker>
        </defs>
        <rect x="0" y="0" width="32" height="32" rx="9" fill="url(#ix-grad)" />
        <g stroke="#E8FF00" strokeWidth="2.4" strokeLinecap="butt">
          {line(16, 16, 8.5, 8.5, [-1, -1])}
          {line(16, 16, 23.5, 8.5, [1, -1])}
          {line(16, 16, 8.5, 23.5, [-1, 1])}
          {line(16, 16, 23.5, 23.5, [1, 1])}
        </g>
        <motion.circle
          cx="16"
          cy="16"
          r="1.9"
          fill="#E8FF00"
          initial={animate ? { scale: 0 } : false}
          animate={{ scale: 1 }}
          transition={{ duration: 0.4, delay: 0.45, ease: [0.76, 0, 0.24, 1] }}
          style={{ transformOrigin: "16px 16px" }}
        />
      </svg>
      <div className="flex items-baseline gap-1.5">
        <span className="font-display font-semibold tracking-tight text-[17px] text-ink leading-none">
          Instantxfer
        </span>
        <span className="text-[9px] tracking-[0.22em] uppercase font-bold text-forest bg-lime px-1.5 py-0.5 rounded">
          CashApp
        </span>
      </div>
    </div>
  );
}
